import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { getLabelData, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function LabelPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [position, setPosition] = useState('top');
    const printRef = useRef(null);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [id]);

    const fetchData = async () => {
        try {
            const response = await getLabelData(id);
            setData(response.data);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement des données'));
            navigate('/masters');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-6">
                <Skeleton className="h-8 w-48 mb-6" />
                <Skeleton className="h-64" />
            </div>
        );
    }

    if (!data) return null;

    const { master, project, company } = data;

    const buildVersionLines = () => {
        if (!master.audio_tracks?.length) return '-';
        return master.audio_tracks.map(t => {
            let line = t.language;
            if (t.subtitles?.length > 0) {
                const stPart = t.subtitles.map(s => `ST${s.substring(0, 2).toUpperCase()}`).join(' ');
                line += ' ' + stPart;
            }
            return line;
        }).join(', ');
    };

    const durationMinutes = () => {
        const tc = master.duration_tc || '00:00:00:00';
        const parts = tc.split(':');
        const h = parseInt(parts[0]) || 0;
        const m = parseInt(parts[1]) || 0;
        return h * 60 + m;
    };

    const LabelContent = () => (
        <div className="label-unit" style={{
            width: '10cm',
            height: '6cm',
            boxSizing: 'border-box',
            border: 'none',
            backgroundColor: '#fff',
            color: '#000',
            fontFamily: 'Arial, Helvetica, sans-serif',
            padding: '3mm',
            position: 'relative',
            display: 'inline-block',
            verticalAlign: 'top',
            overflow: 'hidden',
        }}>
            {/* Header: Company + Version */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1mm' }}>
                <span style={{ fontSize: '14pt', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {company?.name || 'N/A'}
                </span>
                <span style={{ fontSize: '10pt', fontWeight: '600' }}>
                    Version {master.version || '1'}
                </span>
            </div>

            {/* Title */}
            <div style={{ fontSize: '11pt', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '1.5mm', lineHeight: '1.2' }}>
                {project?.title || 'N/A'}
            </div>

            {/* Filename */}
            <div style={{ fontSize: '7pt', fontFamily: 'Courier New, monospace', marginBottom: '2mm', wordBreak: 'break-all', lineHeight: '1.2' }}>
                {master.name}
            </div>

            {/* Info grid */}
            <div style={{ fontSize: '8.5pt', lineHeight: '1.5' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span><b>Durée :</b> {durationMinutes()} minutes</span>
                    <span><b>N° disque :</b> {master.disk_number}</span>
                </div>
                <div>
                    <b>Protection KDM :</b> {master.encrypted ? 'OUI' : 'NON'}
                </div>
                <div>
                    <b>Date :</b> {format(new Date(), 'dd/MM/yyyy', { locale: fr })}
                </div>
                <div>
                    <b>Version :</b> {buildVersionLines()}
                </div>
            </div>

            {/* Footer */}
            <div style={{
                position: 'absolute',
                bottom: '2mm',
                left: '3mm',
                right: '3mm',
                borderTop: '0.5px solid #ccc',
                paddingTop: '1.5mm',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3mm',
                fontSize: '6.5pt',
                color: '#555',
                lineHeight: '1.4',
            }}>
                <img src="/mediaflow-logo.png" alt="Mediaflow" style={{ height: '10px' }} />
                <span>32, rue Titon - 75011 Paris / Tél. : +33 1 43 79 41 89 / gestion@mediaflow.fr</span>
            </div>
        </div>
    );

    const topMargin = position === 'top' ? '10mm' : '75mm';

    return (
        <div className="min-h-screen bg-background">
            {/* Control Bar */}
            <div className="no-print p-4 bg-card border-b border-border flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                    <Button variant="ghost" size="icon" aria-label="Retour au master" onClick={() => navigate(`/masters/${id}`)} data-testid="back-button">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="min-w-0">
                        <h1 className="font-heading text-xl font-bold">IMPRESSION ÉTIQUETTE</h1>
                        <p className="text-sm text-muted-foreground truncate max-w-md" title={master.name}>{master.name}</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Label htmlFor="f-position" className="text-sm whitespace-nowrap">Position :</Label>
                        <Select value={position} onValueChange={setPosition}>
                            <SelectTrigger id="f-position" className="w-[180px] sm:w-[220px]" data-testid="position-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="top">1 - Étiquettes à 1cm</SelectItem>
                                <SelectItem value="offset">2 - Étiquettes à 7,5cm</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={handlePrint} data-testid="print-button">
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimer
                    </Button>
                </div>
            </div>

            {/* Preview */}
            <div className="no-print p-6">
                <p className="text-sm text-muted-foreground mb-4">
                    Aperçu : 2 étiquettes côte à côte (10cm x 6cm) — Position : {position === 'top' ? 'à 1cm du haut' : 'à 7,5cm du haut'}
                </p>
                <div className="bg-white rounded-lg p-8 inline-block mx-auto" style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ paddingTop: position === 'offset' ? '60px' : '8px' }}>
                        <div style={{ display: 'flex', gap: '4mm' }}>
                            <LabelContent />
                            <LabelContent />
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Area */}
            <div className="print-area hidden print:block" ref={printRef}>
                <div style={{ paddingTop: topMargin, display: 'flex', gap: '4mm' }}>
                    <LabelContent />
                    <LabelContent />
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    html, body {
                        background: #fff !important;
                        color: #000 !important;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .print-area, .print-area * {
                        visibility: visible;
                    }
                    .print-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        display: block !important;
                        background: #fff !important;
                    }
                    .label-unit {
                        width: 10cm !important;
                        height: 6cm !important;
                        page-break-inside: avoid;
                        break-inside: avoid;
                        background: #fff !important;
                        color: #000 !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                }
            `}</style>
        </div>
    );
}
